import React, { useContext, useState, useEffect } from 'react';
import { UserContext } from '../contexts/UserContext';
import { hasRolePermission } from '../utility-services/rolePermissions';
import { SrxApi } from '../api/SrxApi';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import {
    Row,
    Col,
    Card,
    Button,
    Input,
    Table,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from 'reactstrap';
import { Icon } from '@iconify/react';
import dotsHorizontal from '@iconify/icons-mdi/dots-horizontal';
import pencilIcon from '@iconify/icons-mdi/pencil';
import deleteIcon from '@iconify/icons-mdi/delete';

const Notification = () => {
    const NOTIFICATION_TYPES = {
        NONE: '',
        LOCK_IN: '1',
        LOCK_OUT: '2',
        ALERT: '3',
    };

    const getNotificationType = (notification) => {
        const apiType = String(notification?.type ?? '').trim().toLowerCase();
        if (apiType === 'lockin') {
            return NOTIFICATION_TYPES.LOCK_IN;
        }
        if (apiType === 'lockout') {
            return NOTIFICATION_TYPES.LOCK_OUT;
        }
        if (apiType === 'alert') {
            return NOTIFICATION_TYPES.ALERT;
        }
        if (notification?.lockOut ?? notification?.LockOut) {
            return NOTIFICATION_TYPES.LOCK_OUT;
        }
        return NOTIFICATION_TYPES.LOCK_IN;
    };

    const formatNotificationTypeLabel = (type) => {
        const normalizedType = String(type ?? '').trim().toLowerCase();
        if (!normalizedType) return '-';
        return normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1);
    };

    const getNotificationStatusLabel = (notification) => (notification?.active ? 'Active' : 'Inactive');

    const { user, roles } = useContext(UserContext);
    const canCreateNotification = hasRolePermission(roles, 'canCreateManageNotification');
    const canEditNotification = hasRolePermission(roles, 'canEditManageNotification');
    const canDeleteNotification = hasRolePermission(roles, 'canDeleteManageNotification');
    const history = useHistory();

    // States for the current form
    const [id, setId] = useState(0);
    const [title, setTitle] = useState('');
    const [bodyHtml, setBodyHtml] = useState('');
    // Make DisplayFrom/DisplayTo optional (empty by default)
    const [displayFrom, setDisplayFrom] = useState('');
    const [displayTo, setDisplayTo] = useState('');
    const [active, setActive] = useState(true);
    const [notificationType, setNotificationType] = useState(NOTIFICATION_TYPES.NONE);
    const [loading, setLoading] = useState(false);

    // State for managing notifications list
    const [notifications, setNotifications] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    
    // State to toggle between showing the list or form
    const [showForm, setShowForm] = useState(false);

    // Fetch notifications on component mount
    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const resp = await SrxApi.get('/GetAllNotification');
                setNotifications(resp.data);
            } catch (err) {
                toast.error('Error fetching notifications');
            }
        };

        fetchNotifications();
    }, []);

    const filteredNotifications = () => {
        if (!searchTerm) return notifications;
        const s = searchTerm.toLowerCase();
        return notifications.filter((n) => (n.title || '').toLowerCase().includes(s) || (n.bodyHtml || '').toLowerCase().includes(s));
    };

    // Validation for form
    const validate = () => {
        if (!title || !title.trim()) {
            toast.error('Title is required');
            return false;
        }
        if (!bodyHtml || !bodyHtml.trim()) {
            toast.error('Body is required');
            return false;
        }
        if (!notificationType) {
            toast.error('Type is required');
            return false;
        }
        // DisplayFrom/DisplayTo are optional. Allow any of: both empty, only from, only to, or both.
        // If a date is provided, ensure it's valid. If both provided, ensure from <= to.
        if (displayFrom) {
            const fromCheck = new Date(displayFrom);
            if (isNaN(fromCheck.getTime())) {
                toast.error('DisplayFrom is not a valid date');
                return false;
            }
        }
        if (displayTo) {
            const toCheck = new Date(displayTo);
            if (isNaN(toCheck.getTime())) {
                toast.error('DisplayTo is not a valid date');
                return false;
            }
        }
        if (displayFrom && displayTo) {
            const from = new Date(displayFrom);
            const to = new Date(displayTo);
            if (from > to) {
                toast.error('DisplayFrom must be before DisplayTo');
                return false;
            }
        }
        return true;
    };

    // Submit form data to create or update a notification
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const dto = {
            Id: id,
            Title: title,
            BodyHtml: bodyHtml,
            type: Number(notificationType),
            // Pass ISO strings only when selected; otherwise send null
            DisplayFrom: displayFrom ? new Date(displayFrom).toISOString() : null,
            DisplayTo: displayTo ? new Date(displayTo).toISOString() : null,
            Active: active,
            // LockOut: notificationType === NOTIFICATION_TYPES.LOCK_OUT,
        };

        try {
            setLoading(true);
            let resp;
            if (id) {
                // If ID exists, we are updating the notification
                resp = await SrxApi.put(`/UpdateNotification/${id}`, dto); // Assuming your API has a PUT endpoint for updating
                toast.success('Notification updated');
            } else {
                // If no ID exists, we are creating a new notification
                resp = await SrxApi.post('/CreateNotification', dto);
                toast.success('Notification created');
            }

            setLoading(false);
            // Fetch updated list of active notifications
            const updatedNotifications = await SrxApi.get('/GetAllNotification');
            setNotifications(updatedNotifications.data);

            // Clear form and hide
            resetForm();
            setShowForm(false);
        } catch (err) {
            setLoading(false);
            const msg = err.response?.data?.message || err.message || 'Error submitting notification';
            toast.error(msg);
        }
    };

    // Reset form fields to defaults
    const resetForm = () => {
        setId(0);
        setTitle('');
        setBodyHtml('');
        setDisplayFrom('');
        setDisplayTo('');
        setActive(true);
        setNotificationType(NOTIFICATION_TYPES.NONE);
        setLoading(false);
    };

    // Edit notification
    const handleEdit = (notification) => {
        setId(notification.id);
        setTitle(notification.title);
        setBodyHtml(notification.bodyHtml);
        // Safely handle null/empty display dates
        setDisplayFrom(notification.displayFrom ? notification.displayFrom.slice(0, 16) : '');
        setDisplayTo(notification.displayTo ? notification.displayTo.slice(0, 16) : '');
        setActive(Boolean(notification.active));
        setNotificationType(getNotificationType(notification));
        setShowForm(true);  // Show the form when editing
    };

    // Open add form and reset any previous values
    const handleAdd = () => {
        resetForm();
        setShowForm(true);
    };

    // Cancel and reset form
    const handleCancel = () => {
        resetForm();
        setShowForm(false);
    };

    // Delete notification
    const handleDelete = async (id) => {
        try {
            const confirmDelete = window.confirm('Are you sure you want to delete this notification?');
            if (confirmDelete) {
                await SrxApi.delete(`/DeleteNotification/${id}`);
                toast.success('Notification deleted');
                // Fetch updated list of active notifications
                const updatedNotifications = await SrxApi.get('/GetAllNotification');
                setNotifications(updatedNotifications.data);
            }
        } catch (err) {
            toast.error('Error deleting notification');
        }
    };

    // (Activation/deactivation removed) Actions are Edit and Delete only

    return (
        <div className="mt-4" style={{ width: '100%', minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
            <div className="mb-3">
                <h3 className="text-primary mb-0">Manage Notifications</h3>
            </div>

            {/* Button to toggle form and search */}
            <div className="d-flex flex-wrap mb-3 align-items-center justify-content-between" style={{ gap: '12px', width: '100%' }}>
                <div style={{ flex: '0 0 auto' }}>
                    {!showForm && canCreateNotification && (
                        <Button color="success" onClick={() => handleAdd()}>
                            <Icon icon={pencilIcon} className="mr-1" /> Add Notification
                        </Button>
                    )}
                </div>
                {/* <div style={{ flex: '1 1 260px', maxWidth: '300px', marginLeft: 'auto', minWidth: 0 }}>
                    <Input
                        placeholder="Search notifications..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div> */}
            </div>

            {/* Show the form if showForm is true */}
            {showForm && (
                <div className="card shadow-sm mb-4">
                    <div className="card-header bg-primary text-white">
                        <h5>{id ? 'Edit Notification' : 'Add New Notification'}</h5>
                    </div>
                    <form onSubmit={handleSubmit} className="card-body">
                        {/* Id (read-only) hidden by design */}

                        <div className="form-group">
                            <label>Title</label>
                            <input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label>Body (HTML)</label>
                            <textarea className="form-control" rows={6} value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} />
                        </div>
                        <div className="form-row">
                            <div className="form-group col-md-6">
                                <label>Display From</label>
                                <input type="datetime-local" className="form-control" value={displayFrom} onChange={e => setDisplayFrom(e.target.value)} />
                            </div>
                            <div className="form-group col-md-6">
                                <label>Display To</label>
                                <input type="datetime-local" className="form-control" value={displayTo} onChange={e => setDisplayTo(e.target.value)} />
                            </div>
                        </div>
                        <div className="form-group">
                            <div
                                className="form-check d-flex align-items-center"
                                style={{ gap: '8px', minHeight: '38px', paddingLeft: 0 }}
                            >
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="activeCheck"
                                    checked={active}
                                    onChange={e => setActive(e.target.checked)}
                                    style={{ position: 'static', margin: 0 }}
                                />
                                <label className="form-check-label mb-0" htmlFor="activeCheck">Active</label>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Type</label>
                            <Input
                                type="select"
                                value={notificationType}
                                onChange={(e) => setNotificationType(e.target.value)}
                            >
                                <option value="">Select type</option>
                                <option value={NOTIFICATION_TYPES.LOCK_IN}>Lock In</option>
                                <option value={NOTIFICATION_TYPES.LOCK_OUT}>Lock Out</option>
                                <option value={NOTIFICATION_TYPES.ALERT}>Alert</option>
                            </Input>
                        </div>
                        <button className="btn btn-primary" type="submit" disabled={loading}>{loading ? 'Saving...' : 'Submit'}</button>
                        <button className="btn btn-secondary ml-2" type="button" onClick={() => handleCancel()}>Cancel</button>
                    </form>
                </div>
            )}

            {/* Notifications list */}
            {!showForm && (
                <Card className="shadow-sm" style={{ width: '100%', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                    <div className="card-header bg-primary text-white">
                        <h5>Notifications List</h5>
                    </div>
                    <div className="card-body p-0" style={{ width: '100%', minWidth: 0 }}>
                        <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                            <Table
                                striped
                                style={{
                                    minWidth: '760px',
                                    width: '100%',
                                    maxWidth: 'none',
                                    marginBottom: 0,
                                }}
                            >
                            <thead>
                                <tr>
                                    <th className="text-nowrap px-3">Title</th>
                                    <th className="text-nowrap px-3">Display From</th>
                                    <th className="text-nowrap px-3">Display To</th>
                                    <th className="text-nowrap px-3">Status</th>
                                    <th className="text-nowrap px-3">Type</th>
                                    <th className="text-nowrap px-3" style={{ width: 100 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredNotifications().map((notification) => (
                                    <tr key={notification.id}>
                                        <td className="text-nowrap px-3">{notification.title}</td>
                                        <td className="text-nowrap px-3">{notification.displayFrom ? new Date(notification.displayFrom).toLocaleString() : '-'}</td>
                                        <td className="text-nowrap px-3">{notification.displayTo ? new Date(notification.displayTo).toLocaleString() : '-'}</td>
                                        <td className="text-nowrap px-3">{getNotificationStatusLabel(notification)}</td>
                                        <td className="text-nowrap px-3">{formatNotificationTypeLabel(notification.type)}</td>
                                        <td className="text-nowrap px-3">
                                            <UncontrolledDropdown>
                                                <DropdownToggle caret color="link">
                                                    <Icon icon={dotsHorizontal} width="20" height="20" />
                                                </DropdownToggle>
                                                <DropdownMenu right>
                                                    {canEditNotification && (
                                                        <DropdownItem onClick={() => handleEdit(notification)}>
                                                            <Icon icon={pencilIcon} className="mr-2" /> Edit
                                                        </DropdownItem>
                                                    )}
                                                    {/* Activate/Deactivate removed - only Edit and Delete actions remain */}
                                                    {canDeleteNotification && (
                                                        <DropdownItem onClick={() => handleDelete(notification.id)}>
                                                            <Icon icon={deleteIcon} className="mr-2" /> Delete
                                                        </DropdownItem>
                                                    )}
                                                </DropdownMenu>
                                            </UncontrolledDropdown>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            </Table>
                        </div>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default Notification;
